import { ref, Ref } from 'vue'
import navigatorDataManager from '../data/NavigatorDataManager'

import dataQueryManager from '../network/DataQueryManager'
import dataStorageManager, { BucketDefaultIDs } from '../data/DataStorageManager'
import { IUser, UserRoles } from '../../services/api-services/UsersServices'
import { rutify } from '../../services/apiUtils'
import { isCompliantPassword } from '../../services/utils/PasswordPolicy'

export interface LoggedInUser {
  bloqueo: number
  bloqueo_fecha: string
  clave: string
  email: string
  id_usuario: number
  nombre_completo: string
  notificar: any
  rol: UserRoles
  rut: string
  vigente: number
  telefono?: string
  id_gestor_fotografia?: number
  id_gestor_ci?: number
}

interface loggedUserData extends LoggedInUser {
  lastLogin: Date
  keepLogged: boolean
}

type SubscriberCallback = (user: LoggedInUser | null) => void

// Manejador de identidad
// Se encarga de gestionar la identidad del usuario actual, incluyendo su rol, permisos y cualquier otra información relevante.
class IdentityManager {
  private user: loggedUserData | null = null
  private subscribers: Set<SubscriberCallback> = new Set()
  public loadPromise: Promise<boolean> | null = null
  private promiseActivator: ((resp: boolean) => void) | null = null
  private activeArmadorId: number | null = null
  private activeArmadorIdRef: Ref<number | null> = ref(null)
  // Política de clave: se activa sólo en login fresco cuando la clave ingresada
  // no cumple la política. Vive en memoria — al refrescar pestaña se pierde y
  // el usuario debe pasar nuevamente por login.
  private requiresPasswordChangeRef: Ref<boolean> = ref(false)
  private temporalPassword: string | null = null

  constructor() {
    this.loadPromise = new Promise<boolean>((resolve) => {
      this.promiseActivator = resolve
    })
  }

  startup() {
    dataStorageManager.subscribeBucket<IUser>(BucketDefaultIDs.USERS, (data) => {})
    const lastUser = navigatorDataManager.registerData('loggedUser', 'private', null) as loggedUserData | null
    // La sesion se mantiene si el usuario lo indico o si la ultima conexion fue hace menos de 60 minutos
    if (
      lastUser &&
      lastUserCheckup(lastUser) &&
      (lastUser.keepLogged || new Date().getTime() - new Date(lastUser.lastLogin).getTime() < 60 * 60 * 1000)
    ) {
      this.updatePreviousSession(lastUser)
      // Update data of the user
    } else {
      this.user = null
      navigatorDataManager.updateData('loggedUser', null)
      if (this.promiseActivator) {
        this.promiseActivator(this.user !== null)
        this.promiseActivator = null
      }
    }
  }

  async updatePreviousSession(lastUser: loggedUserData): Promise<boolean> {
    // Restaurar sesión inmediatamente con los datos locales guardados para evitar condiciones de carrera
    this.user = { ...lastUser, lastLogin: new Date() }
    this.notifySubscribers()

    if (this.promiseActivator) {
      this.promiseActivator(true)
      this.promiseActivator = null
    }

    // Validación y sincronización en segundo plano con el bucket de usuarios
    try {
      const users = await dataStorageManager.getBucketData<IUser>(BucketDefaultIDs.USERS)
      if (users && users.length > 0) {
        const matchedUser = users.find((u) => u.id_usuario === lastUser.id_usuario)
        if (matchedUser) {
          if (matchedUser.vigente === 0 || matchedUser.bloqueo === 1) {
            this.logOut()
          } else {
            this.user = { ...matchedUser, lastLogin: new Date(), keepLogged: lastUser.keepLogged }
            this.notifySubscribers()
          }
        }
      }
    } catch (e) {
      console.error('[IdentityManager] Error en sincronizacion de sesion previa:', e)
    }
    return true
  }

  async logUser(rut: string, password: string, keepLogged: boolean = false): Promise<LoggedInUser | null> {
    try {
      const body = {
        op_login_usuario: {
          rut: rutify(rut),
          password: password,
        },
      }

      const response = await dataQueryManager.addQuery({
        query: `${import.meta.env.VITE_BASE_URL}/login_rol_menu/1/op_login_usuario`,
        operation: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        params: [],
        body: body,
      })

      const loggedInUser = response.data

      let returnValue: LoggedInUser | null = null
      if (Array.isArray(loggedInUser) && loggedInUser.length > 0) {
        returnValue = loggedInUser[0]
      } else if (loggedInUser && typeof loggedInUser === 'object') {
        returnValue = loggedInUser as LoggedInUser
      } else {
        returnValue = null
      }

      this.user = returnValue ? { ...returnValue, lastLogin: new Date(), keepLogged: keepLogged } : null
      if (returnValue && !isCompliantPassword(password)) {
        this.requiresPasswordChangeRef.value = true
        this.temporalPassword = password
      } else {
        this.requiresPasswordChangeRef.value = false
        this.temporalPassword = null
      }
      this.notifySubscribers()
      return returnValue
    } catch (error) {
      console.error('Error logging in user:', error)
      this.user = null
      return null
    }
  }

  async logTest(rut: string, password: string): Promise<LoggedInUser | null> {
    try {
      const body = {
        op_login_usuario: {
          rut: rutify(rut),
          password: password,
        },
      }
      const response = await dataQueryManager.addQuery({
        query: `${import.meta.env.VITE_BASE_URL}/login_rol_menu/1/op_login_usuario`,
        operation: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        params: [],
        body: body,
      })
      const loggedInUser = response.data
      let returnValue: LoggedInUser | null = null
      if (Array.isArray(loggedInUser) && loggedInUser.length > 0) {
        returnValue = loggedInUser[0]
      } else if (loggedInUser && typeof loggedInUser === 'object') {
        returnValue = loggedInUser as LoggedInUser
      } else {
        returnValue = null
      }
      return returnValue
    } catch (error) {
      console.error('Error logging in user:', error)
      this.user = null
      return null
    }
  }

  logOut() {
    this.user = null
    this.setActiveArmadorId(null)
    this.requiresPasswordChangeRef.value = false
    this.temporalPassword = null
    this.notifySubscribers()
  }

  getRequiresPasswordChange(): Ref<boolean> {
    return this.requiresPasswordChangeRef
  }

  getTemporalPassword(): string | null {
    return this.temporalPassword
  }

  clearPasswordChangeRequirement() {
    this.requiresPasswordChangeRef.value = false
    this.temporalPassword = null
  }

  subscribe(callback: SubscriberCallback) {
    this.subscribers.add(callback)
    if (this.user) callback(this.user)
  }

  private notifySubscribers() {
    if (this.user) {
      const noPassUser: LoggedInUser = { ...this.user }
      noPassUser.clave = ''
      navigatorDataManager.updateData('loggedUser', noPassUser)
      this.subscribers.forEach((callback) => callback(noPassUser))
    } else {
      navigatorDataManager.updateData('loggedUser', null)
      this.subscribers.forEach((callback) => callback(null))
    }
  }

  getUser(): LoggedInUser | null {
    return this.user
  }

  patchUser(patch: Partial<LoggedInUser>) {
    if (!this.user) return
    this.user = { ...this.user, ...patch }
    this.notifySubscribers()
  }

  setActiveArmadorId(id: number | null) {
    this.activeArmadorId = id
    this.activeArmadorIdRef.value = id
  }

  getActiveArmadorId(): number | null {
    return this.activeArmadorId
  }

  getRefActiveArmadorId(): Ref<number | null> {
    return this.activeArmadorIdRef
  }
}

function lastUserCheckup(user: object): boolean {
  return 'id_usuario' in user && 'email' in user
}

const identityManager = new IdentityManager()
export default identityManager
